import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TICKET_STATUS, type TicketListItem } from '@his/shared';
import { get, qs } from '@/lib/api';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Select, statusLabel } from '@/components/ui/primitives';
import { TICKET_STATUS_TONE, type DeskEndpoints } from './desk-api';

/**
 * The ticket queue.
 *
 * Ordered by last activity, not by age: a two-week-old ticket somebody replied
 * to this morning needs attention before a quiet one filed at nine.
 */
export function TicketListView({
  endpoints,
  onOpen,
  showTenant = true,
  extraFilters,
  onResetExtraFilters,
  fixedTenantId,
}: {
  endpoints: DeskEndpoints;
  onOpen: (ticket: TicketListItem) => void;
  showTenant?: boolean;
  extraFilters?: React.ReactNode;
  onResetExtraFilters?: () => void;
  fixedTenantId?: string;
}) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  const tickets = useQuery({
    queryKey: ['tickets', endpoints.tickets, status, q, fixedTenantId],
    queryFn: () =>
      get<TicketListItem[]>(
        `${endpoints.tickets}${qs({
          status: status || undefined,
          q: q || undefined,
          tenantId: fixedTenantId,
        })}`,
      ),
  });

  const filtered = Boolean(status || q || (onResetExtraFilters && fixedTenantId));

  return (
    <>
      <SearchFilterBar
        search={{ value: q, onChange: setQ, placeholder: 'Search subject, number or reporter' }}
        filters={
          <>
            <FilterField label="Status">
              <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Any status</option>
                {TICKET_STATUS.map((value) => (
                  <option key={value} value={value}>
                    {statusLabel(value)}
                  </option>
                ))}
              </Select>
            </FilterField>
            {extraFilters}
          </>
        }
        onReset={
          filtered
            ? () => {
                setStatus('');
                setQ('');
                onResetExtraFilters?.();
              }
            : undefined
        }
      />

      <DataTable
        rows={tickets.data}
        loading={tickets.isLoading} refreshing={tickets.isFetching}
        error={tickets.error}
        rowKey={(row) => row.id}
        onRowClick={onOpen}
        empty="No tickets here yet."
        columns={[
          {
            header: 'Ticket',
            sort: (row) => row.subject,
            cell: (row) => (
              <div>
                <div className="font-medium">{row.subject}</div>
                <div className="text-xs text-muted-foreground">
                  {row.number}
                  {showTenant ? ` · ${row.tenantName}` : ''} · {row.raisedByName}
                </div>
              </div>
            ),
          },
          {
            header: 'Status',
            sort: (row) => row.status,
            cell: (row) => (
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant={TICKET_STATUS_TONE[row.status] ?? 'secondary'}>
                  {statusLabel(row.status)}
                </Badge>
                {row.escalatedAt && <Badge variant="destructive">{statusLabel('escalated')}</Badge>}
              </div>
            ),
          },
          { header: 'Priority', sort: (row) => row.priority, cell: (row) => humanize(row.priority, 'priority') },
          { header: 'Replies', align: 'right', sort: (row) => row.messageCount, cell: (row) => row.messageCount },
          { header: 'Last activity', sort: (row) => row.lastActivityAt, cell: (row) => dateTime(row.lastActivityAt) },
        ]}
      />
    </>
  );
}
